import { clsx } from 'clsx';
import { Button, Card, Flex, Text } from '@nula/core-ui';
import type { UseCase } from '../../types/onboarding';
import styles from './about-step.module.css';

export interface AboutStepProps {
  useCase: UseCase | null;
  referralSource: string | null;
  onUseCase: (v: UseCase) => void;
  onReferral: (v: string) => void;
  onContinue: () => void;
  onSkip: () => void;
}

interface UseCaseOption {
  value: UseCase;
  label: string;
  icon: React.ReactNode;
}

// Inline SVG children are a permitted exception per CLAUDE.md (no icon
// primitive exists for these custom glyphs).
const BriefcaseIcon = () => (
  <svg className={styles.useCaseIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
    <line x1="12" y1="12" x2="12" y2="12" strokeLinecap="round" strokeWidth={2} />
  </svg>
);

const AcademicCapIcon = () => (
  <svg className={styles.useCaseIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
    <path d="M12 3L2 8l10 5 10-5-10-5z" />
    <path d="M6 10.5v5.5a6 6 0 0012 0v-5.5" />
    <path d="M22 8v6" strokeLinecap="round" />
  </svg>
);

const PersonIcon = () => (
  <svg className={styles.useCaseIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
  </svg>
);

// Small checkmark for the selected card badge + selected referral chip
// (inline SVG — allowed exception per CLAUDE.md).
const CheckMark = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden>
    <polyline points="3,8.5 6.5,12 13,4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const USE_CASE_OPTIONS: UseCaseOption[] = [
  { value: 'work', label: 'Work', icon: <BriefcaseIcon /> },
  { value: 'school', label: 'School', icon: <AcademicCapIcon /> },
  { value: 'personal', label: 'Personal', icon: <PersonIcon /> },
];

// value = slug stored server-side (matches the DB constraint); label = display.
const REFERRAL_OPTIONS: { value: string; label: string }[] = [
  { value: 'friend', label: 'A friend' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'search', label: 'Search' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'newsletter', label: 'A newsletter' },
  { value: 'other', label: 'Other' },
];

/** Activate the Card tile on Enter or Space, like a native button. */
function activateOnKey(handler: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };
}

export function AboutStep({
  useCase,
  referralSource,
  onUseCase,
  onReferral,
  onContinue,
  onSkip,
}: AboutStepProps) {
  return (
    <Flex vertical gap="lg">
      <Flex vertical>
        <Text className={styles.eyebrow}>One last thing</Text>
        <Text className={styles.title}>
          How will you <em className={styles.titleEm}>use Nula?</em>
        </Text>
        <Text type="secondary" className={styles.subtitle}>
          {"We'll tailor your space to fit. You can change this anytime."}
        </Text>
      </Flex>

      <div className={styles.useCaseGrid}>
        {USE_CASE_OPTIONS.map((opt) => {
          const selected = useCase === opt.value;
          return (
            <Card
              key={opt.value}
              hoverable
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              onClick={() => onUseCase(opt.value)}
              onKeyDown={activateOnKey(() => onUseCase(opt.value))}
              className={clsx(styles.useCaseCard, selected && styles.selected)}
            >
              {selected && (
                <span className={styles.checkBadge} data-testid={`usecase-check-${opt.value}`}>
                  <CheckMark />
                </span>
              )}
              <span className={styles.useCaseIconWrap}>{opt.icon}</span>
              <span className={styles.useCaseLabel}>{opt.label}</span>
            </Card>
          );
        })}
      </div>

      <Flex vertical gap="sm">
        <Text type="secondary" className={styles.sectionLabel}>
          How did you hear about us?
        </Text>
        <Flex wrap gap="sm">
          {REFERRAL_OPTIONS.map((opt) => {
            const selected = referralSource === opt.value;
            return (
              <Button
                key={opt.value}
                pill
                size="md"
                variant={selected ? 'primary' : 'default'}
                aria-pressed={selected}
                icon={selected ? <CheckMark /> : undefined}
                onClick={() => onReferral(opt.value)}
                className={styles.referralChip}
              >
                {opt.label}
              </Button>
            );
          })}
        </Flex>
      </Flex>

      <Flex align="center" gap="md">
        <Button variant="primary" size="lg" block onClick={onContinue}>Enter Nula</Button>
        <Button variant="text" size="sm" onClick={onSkip} className={styles.skip}>Skip</Button>
      </Flex>
    </Flex>
  );
}
